import React, { useState } from 'react';
import { adminService } from '../services/api';
import { maskCNPJ, isValidCNPJ } from '../utils/masks'; // #231
import Button from './ui/Button';
import './AdminPlatform.css';

/**
 * #79/#148 — Administração da plataforma (super-admin / dono do Qaa.sa).
 *
 * Hoje só a ferramenta "Criar conta": provisiona um tenant novo + o 1º
 * usuário admin dele, com o plano escolhido. Útil pra criar as próprias
 * empresas do dono (plano `founder` = ilimitado/sem cobrança) ou cortesias —
 * sem passar pelo cadastro público (que recusa e-mails de super-admin).
 *
 * Esta tela só é montada/exibida quando `user.is_superadmin` (gate no App.jsx);
 * o backend valida de novo via `_require_superadmin` (defesa em profundidade).
 */

const PLANOS = ['founder', 'business', 'pro', 'starter', 'trial', 'cortesia', 'beta', 'enterprise'];
const TIPOS = ['regular', 'founder', 'cortesia', 'beta'];

function AdminPlatform() {
  const [nome, setNome] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminNome, setAdminNome] = useState('');
  const [adminSenha, setAdminSenha] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [plano, setPlano] = useState('founder');
  const [accountType, setAccountType] = useState('founder');
  const [isInternalTest, setIsInternalTest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const senhaCurta = adminSenha.length > 0 && adminSenha.length < 8;
  const cnpjInvalido = cnpj.trim().length > 0 && !isValidCNPJ(cnpj);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!nome.trim() || !adminEmail.trim() || !adminSenha) {
      setError('Preencha o nome da empresa, o e-mail e a senha do admin.');
      return;
    }
    if (senhaCurta) {
      setError('A senha do admin precisa ter ao menos 8 caracteres.');
      return;
    }
    if (cnpjInvalido) {
      setError('CNPJ inválido — confira os números ou deixe em branco.');
      return;
    }
    setLoading(true);
    try {
      const data = await adminService.criarConta({
        nome: nome.trim(),
        admin_email: adminEmail.trim(),
        admin_nome: adminNome.trim(),
        admin_senha: adminSenha,
        cnpj: cnpj.trim() || null,
        subscription_plan: plano,
        account_type: accountType,
        is_internal_test: isInternalTest,
      });
      setResult(data);
      // limpa o formulário (inclusive a senha) após sucesso
      setNome('');
      setAdminEmail('');
      setAdminNome('');
      setAdminSenha('');
      setCnpj('');
      setIsInternalTest(false);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-platform">
      <header className="admin-platform-header">
        <h1 className="admin-platform-title">
          <span className="material-symbols-outlined">admin_panel_settings</span>
          Administração da plataforma
        </h1>
        <p className="admin-platform-sub">
          Área exclusiva do dono do Qaa.sa. Crie contas (empresas) com o plano que quiser — ideal
          para suas próprias empresas (plano <strong>founder</strong> = ilimitado, sem cobrança) ou cortesias.
          Não passa pelo cadastro público.
        </p>
      </header>

      <section className="admin-card">
        <h2 className="admin-card-title">Criar nova conta</h2>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-field">
            <label className="admin-label">Nome da empresa *</label>
            <input className="admin-input" value={nome} onChange={(e) => setNome(e.target.value)}
              placeholder="Minha Segunda Empresa LTDA" disabled={loading} />
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">E-mail do admin *</label>
              <input type="email" className="admin-input" value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@empresa.com.br" disabled={loading} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Nome do admin</label>
              <input className="admin-input" value={adminNome}
                onChange={(e) => setAdminNome(e.target.value)} placeholder="Nome e sobrenome" disabled={loading} />
            </div>
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Senha do admin *</label>
              <div className="admin-input-wrap">
                <input type={showPassword ? 'text' : 'password'} className="admin-input" value={adminSenha}
                  onChange={(e) => setAdminSenha(e.target.value)} placeholder="Mínimo 8 caracteres" disabled={loading} />
                <button type="button" className="admin-eye" onClick={() => setShowPassword(!showPassword)}>
                  <span className="material-symbols-outlined">{showPassword ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
              {senhaCurta && <p className="admin-hint-error">Mínimo 8 caracteres.</p>}
            </div>
            <div className="admin-field">
              <label className="admin-label">CNPJ <span className="admin-opt">(opcional)</span></label>
              <input className="admin-input" inputMode="numeric" value={cnpj}
                onChange={(e) => setCnpj(maskCNPJ(e.target.value))} placeholder="00.000.000/0000-00" disabled={loading} />
              {cnpjInvalido && <p className="admin-hint-error">CNPJ inválido.</p>}
            </div>
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Plano</label>
              <select className="admin-input" value={plano} onChange={(e) => setPlano(e.target.value)} disabled={loading}>
                {PLANOS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="admin-field">
              <label className="admin-label">Tipo de conta</label>
              <select className="admin-input" value={accountType} onChange={(e) => setAccountType(e.target.value)} disabled={loading}>
                {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <label className="admin-check">
            <input type="checkbox" checked={isInternalTest} onChange={(e) => setIsInternalTest(e.target.checked)} disabled={loading} />
            <span>Conta de teste interna (não conta em métricas)</span>
          </label>

          <p className="admin-warn">
            <span className="material-symbols-outlined">key</span>
            Anote a senha em local seguro — ela não é exibida depois. Repasse-a ao admin da conta com cuidado.
          </p>

          {error && <div className="admin-error">{error}</div>}

          {result && (
            <div className="admin-success">
              <strong>Conta criada com sucesso.</strong>
              <ul>
                <li>Empresa: <b>{result.nome}</b> (ID {result.client_id})</li>
                <li>Admin: <b>{result.admin_user?.email}</b></li>
                <li>Plano: <b>{result.subscription_plan}</b>{result.account_type ? ` · ${result.account_type}` : ''}</li>
              </ul>
              <span className="admin-success-note">O admin já pode entrar com o e-mail e a senha definidos acima.</span>
            </div>
          )}

          <div className="admin-actions">
            <Button type="submit" disabled={loading}>
              {loading ? 'Criando…' : 'Criar conta'}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default AdminPlatform;
